/**
 * Evolve Analytics — soft theme: hairline borders, diffuse shadows, 12px cards
 * and 8px controls, a lime accent for primary fills, Manrope type, and a dark
 * sidebar in BOTH modes (the only dark chrome left).
 *
 * `makeTheme(mode)` builds light or dark from one set of role-named tokens
 * (./tokens.js); components read them off `theme.palette.nb` via `useTokens()`.
 */
import { createTheme, useTheme } from '@mui/material/styles';
import { enUS, ruRU } from '@mui/material/locale';
import { tokens, shadowScale } from './tokens';

export { tokens } from './tokens';

/** Role tokens for the mode in force: `const t = useTokens()` → `t.ink`, … */
export const useTokens = () => useTheme().palette.nb;

const CARD_RADIUS = 12;

// ── Theme factory ────────────────────────────────────────────────────────────
export function makeTheme(mode = 'light', language = 'en') {
  const t = tokens(mode);
  const sh = shadowScale(mode);
  const dark = mode === 'dark';

  const shadow = (level = 1) => sh[Math.min(level, sh.length - 1)] ?? sh[1];
  const border = (px = 1, color = t.border) => `${px}px solid ${color}`;
  // Text that sits on a saturated status fill: white on light, ink on dark.
  const onStatus = dark ? t.onAccent : '#FFFFFF';

  const HOVER_TRANS = 'background-color 150ms ease, box-shadow 150ms ease';
  const hoverShift  = { boxShadow: shadow(2) };

  // All 25 MUI elevation slots off the five-rung scale.
  const shadows = Array.from({ length: 25 }, (_, i) => {
    if (i === 0) return 'none';
    if (i <= 2)  return shadow(1);
    if (i <= 4)  return shadow(2);
    if (i <= 8)  return shadow(3);
    return shadow(4);
  });

  return createTheme({

    // ── Palette ────────────────────────────────────────────────────────────
    palette: {
      mode,
      background: { default: t.canvas, paper: t.surface },
      // Primary is the interactive blue, not the lime: MUI paints primary as
      // text, icons, checkboxes and progress, where lime would vanish on
      // white. The lime is applied explicitly to primary *fills* below.
      primary:   { main: t.link,     contrastText: onStatus },
      secondary: { main: t.series[2], contrastText: onStatus },
      error:     { main: t.negative, contrastText: onStatus },
      warning:   { main: t.warning,  contrastText: onStatus },
      success:   { main: t.positive, contrastText: onStatus },
      info:      { main: t.info,     contrastText: onStatus },
      text: {
        primary:   t.ink,
        secondary: t.inkMuted,
        disabled:  t.inkDim,
      },
      divider: t.border,
      action: { hover: t.raised, selected: t.highlight },
      // Mirrored on dark — see the note in tokens.js.
      grey: t.grey,
      // Role tokens for components: theme.palette.nb.*
      nb: t,
    },

    shadows,
    shape: { borderRadius: 8 },

    // ── Typography ─────────────────────────────────────────────────────────
    typography: {
      fontFamily: "'Manrope', system-ui, sans-serif",
      fontWeightLight:   400,
      fontWeightRegular: 400,
      fontWeightMedium:  500,
      fontWeightBold:    600,
      h1: { fontWeight: 800, letterSpacing: '-0.02em' },
      h2: { fontWeight: 800, letterSpacing: '-0.02em' },
      h3: { fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontWeight: 700, letterSpacing: '-0.01em' },
      h5: { fontWeight: 700, letterSpacing: '-0.01em' },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 600, letterSpacing: '0.01em' },
      body1: { fontWeight: 400 },
      body2: { fontWeight: 400 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
      caption: { fontWeight: 500, fontSize: '0.75rem' },
      overline: { fontWeight: 700, letterSpacing: '0.1em' },
    },

    // ── Component overrides ────────────────────────────────────────────────
    components: {

      MuiCssBaseline: {
        styleOverrides: { body: { backgroundColor: t.canvas } },
      },

      // Paper ───────────────────────────────────────────────────────────────
      MuiPaper: {
        defaultProps: { elevation: 1 },
        styleOverrides: {
          // backgroundImage:none kills MUI's dark-mode elevation overlay, which
          // would otherwise lighten every raised Paper on top of our own scale.
          root: { border: border(), backgroundImage: 'none' },
          rounded: { borderRadius: CARD_RADIUS },
          elevation0: { boxShadow: 'none', border: 'none' },
          elevation1: { boxShadow: shadow(1) },
          elevation2: { boxShadow: shadow(2) },
          elevation3: { boxShadow: shadow(2) },
          elevation4: { boxShadow: shadow(3) },
          elevation8: { boxShadow: shadow(3) },
          elevation16:{ boxShadow: shadow(4) },
          elevation24:{ boxShadow: shadow(4) },
        },
      },

      // Card ────────────────────────────────────────────────────────────────
      MuiCard: {
        defaultProps: { elevation: 1 },
        styleOverrides: {
          root: {
            border: border(),
            borderRadius: CARD_RADIUS,
            boxShadow: shadow(1),
            transition: HOVER_TRANS,
            '&:hover': hoverShift,
          },
        },
      },

      // Button ──────────────────────────────────────────────────────────────
      MuiButton: {
        defaultProps: { disableRipple: true, disableElevation: true },
        styleOverrides: {
          root: { fontWeight: 600, transition: HOVER_TRANS },
          contained: {
            '&:disabled': { backgroundColor: t.raised, color: t.inkDim },
          },
          // The one lime fill: "Подключить источник", "Сохранить", …
          containedPrimary: {
            backgroundColor: t.accent,
            color: t.onAccent,
            '&:hover:not(:disabled)': { backgroundColor: t.accentHover },
          },
          containedError: {
            backgroundColor: t.negative,
            color: onStatus,
            '&:hover:not(:disabled)': { backgroundColor: t.negative, ...hoverShift },
          },
          containedSuccess: {
            backgroundColor: t.positive,
            color: onStatus,
            '&:hover:not(:disabled)': { backgroundColor: t.positive, ...hoverShift },
          },
          outlined: {
            border: border(1, t.borderField),
            backgroundColor: t.surface,
            color: t.ink,
            '&:hover:not(:disabled)': { border: border(1, t.borderStrong), backgroundColor: t.surface },
            '&:disabled': { border: border(), color: t.inkDim },
          },
          outlinedError: {
            borderColor: t.negative,
            color: t.negative,
            '&:hover:not(:disabled)': { borderColor: t.negative, backgroundColor: t.negativeBg },
          },
          outlinedSuccess: {
            borderColor: t.positive,
            color: t.positive,
            '&:hover:not(:disabled)': { borderColor: t.positive, backgroundColor: t.positiveBg },
          },
          text: { '&:hover:not(:disabled)': { backgroundColor: t.raised } },
          sizeLarge:  { padding: '10px 24px', fontSize: '0.95rem' },
          sizeMedium: { padding: '7px 18px' },
          sizeSmall:  { padding: '4px 12px', fontSize: '0.8rem' },
        },
      },

      // IconButton ──────────────────────────────────────────────────────────
      MuiIconButton: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: { transition: HOVER_TRANS, '&:hover': { backgroundColor: t.raised } },
        },
      },

      // TextField / Input ───────────────────────────────────────────────────
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: t.surface,
            '& .MuiOutlinedInput-notchedOutline': {
              border: border(1, t.borderField),
              transition: 'border-color 150ms ease, box-shadow 150ms ease',
            },
            '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
              borderColor: t.iconMuted,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: t.focus,
              borderWidth: '1px',
              boxShadow: `0 0 0 3px ${t.focus}33`,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: t.negative,
              boxShadow: `0 0 0 3px ${t.negative}26`,
            },
          },
          input: { '&::placeholder': { color: t.inkDim, opacity: 1 } },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: '0.875rem',
            color: t.inkMuted,
            '&.Mui-focused': { color: t.link },
            '&.Mui-error':   { color: t.negative },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { fontWeight: 400, fontSize: '0.75rem' } },
      },

      // Chip ────────────────────────────────────────────────────────────────
      // Status chips are the sheet's wash + text pairs ("Готово", "Ошибка").
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, height: 24, fontSize: '0.75rem', borderRadius: 999 },
          colorDefault:   { backgroundColor: t.raised,     color: t.inkMuted },
          colorPrimary:   { backgroundColor: t.infoBg,     color: t.link },
          colorSecondary: { backgroundColor: t.raised,     color: t.ink },
          colorSuccess:   { backgroundColor: t.positiveBg, color: t.positive },
          colorError:     { backgroundColor: t.negativeBg, color: t.negative },
          colorWarning:   { backgroundColor: t.warningBg,  color: t.warning },
          colorInfo:      { backgroundColor: t.infoBg,     color: t.info },
          sizeSmall:      { height: 20, fontSize: '0.7rem' },
        },
      },

      // Alert ───────────────────────────────────────────────────────────────
      MuiAlert: {
        styleOverrides: {
          root: { border: border(), borderRadius: CARD_RADIUS, fontWeight: 400 },
          standardError:   { backgroundColor: t.negativeBg, borderColor: 'transparent', color: t.ink, '& .MuiAlert-icon': { color: t.negative } },
          standardSuccess: { backgroundColor: t.positiveBg, borderColor: 'transparent', color: t.ink, '& .MuiAlert-icon': { color: t.positive } },
          standardWarning: { backgroundColor: t.warningBg,  borderColor: 'transparent', color: t.ink, '& .MuiAlert-icon': { color: t.warning  } },
          // The sheet sets info-plaque text in the link colour.
          standardInfo:    { backgroundColor: t.infoBg,     borderColor: 'transparent', color: t.link, '& .MuiAlert-icon': { color: t.info } },
        },
      },

      // Table ───────────────────────────────────────────────────────────────
      MuiTableContainer: {
        styleOverrides: { root: { border: border(), borderRadius: CARD_RADIUS, boxShadow: shadow(1) } },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-root': {
              backgroundColor: t.raised,
              color: t.inkMuted,
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableRow-root': {
              '&:hover': { backgroundColor: t.raised },
              '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: t.highlight },
              '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: border(), fontWeight: 400 },
        },
      },

      // Tabs ────────────────────────────────────────────────────────────────
      MuiTabs: {
        styleOverrides: {
          root: { borderBottom: border() },
          indicator: { height: 2, backgroundColor: t.ink },
        },
      },
      MuiTab: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            fontWeight: 500,
            textTransform: 'none',
            minHeight: 44,
            color: t.inkMuted,
            '&.Mui-selected': { color: t.ink, fontWeight: 600 },
            '&:hover': { color: t.ink, backgroundColor: t.raised },
          },
        },
      },

      // Tooltip — inverse of the page in both modes ───────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: t.ink,
            color: t.surface,
            fontWeight: 500,
            fontSize: '0.75rem',
            padding: '5px 10px',
            borderRadius: 6,
            boxShadow: shadow(2),
          },
          arrow: { color: t.ink },
        },
      },

      // Drawer / AppBar ─────────────────────────────────────────────────────
      MuiDrawer: {
        styleOverrides: { paper: { borderRight: border(), boxShadow: 'none' } },
      },
      MuiAppBar: {
        styleOverrides: { root: { boxShadow: 'none', borderBottom: border() } },
      },

      // Menu / MenuItem ─────────────────────────────────────────────────────
      MuiMenu: {
        styleOverrides: { paper: { border: border(), boxShadow: shadow(3) } },
      },
      MuiMenuItem: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            fontWeight: 400,
            '&:hover': { backgroundColor: t.raised },
            '&.Mui-selected': {
              backgroundColor: t.highlight,
              fontWeight: 600,
              '&:hover': { backgroundColor: t.highlight },
            },
          },
        },
      },

      // ListItemButton ──────────────────────────────────────────────────────
      MuiListItemButton: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: t.raised },
            '&.Mui-selected': {
              backgroundColor: t.highlight,
              fontWeight: 600,
              '&:hover': { backgroundColor: t.highlight },
            },
          },
        },
      },

      // Avatar / Badge ──────────────────────────────────────────────────────
      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: 700, backgroundColor: t.accent, color: t.onAccent },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontWeight: 700,
            fontSize: '0.65rem',
            minWidth: 16,
            height: 16,
            padding: '0 4px',
          },
          colorError: { backgroundColor: t.negative, color: onStatus },
        },
      },

      // Switch ──────────────────────────────────────────────────────────────
      MuiSwitch: {
        styleOverrides: {
          track: {
            opacity: '1 !important',
            backgroundColor: `${t.borderField} !important`,
          },
          switchBase: {
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: `${t.link} !important`,
              opacity: '1 !important',
            },
          },
        },
      },

      // Divider ─────────────────────────────────────────────────────────────
      MuiDivider: {
        styleOverrides: { root: { borderColor: t.border } },
      },

      // Dialog ──────────────────────────────────────────────────────────────
      MuiBackdrop: {
        styleOverrides: { root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: t.overlay } } },
      },
      MuiDialog: {
        styleOverrides: { paper: { border: border(), borderRadius: 16, boxShadow: shadow(4) } },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            fontSize: '1.05rem',
            color: t.ink,
            padding: '18px 24px 8px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: { root: { padding: '12px 24px 20px' } },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            borderTop: border(),
            padding: '12px 24px',
            gap: 8,
          },
        },
      },

      // Popover ─────────────────────────────────────────────────────────────
      MuiPopover: {
        styleOverrides: { paper: { border: border(), boxShadow: shadow(3) } },
      },

      // Feedback ────────────────────────────────────────────────────────────
      MuiSkeleton: {
        styleOverrides: { root: { backgroundColor: t.skeleton } },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { height: 6, borderRadius: 999, backgroundColor: t.raised } },
      },

      // Accordion ───────────────────────────────────────────────────────────
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: border(),
            boxShadow: shadow(1),
            '&:before': { display: 'none' },
            '&.Mui-expanded': { boxShadow: shadow(2), margin: 0 },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            '&.Mui-expanded': { borderBottom: border(), minHeight: 48 },
          },
        },
      },
    },
  }, language === 'ru' ? ruRU : enUS);
}

const theme = makeTheme('light');
export default theme;
